import { EntityFieldInfo, EntityInfo } from '@record-it-npm/synchro-common';
import { Disposable, DisposableContainer } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { AnyEntityWithEntityName } from '../AnyEntityWithEntityName';
import { EntityManagerBaseEntity } from '../EntityManagerBaseEntity';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { EntityRepositoryHooks } from '../EntityRepository/EntityRepository';
import { RepositoryByEntityName, EntityRepositoryContainer } from '../EntityRepositoryContainer/EntityRepositoryContainer';
import { HooksContainer } from '../HooksContainer/HooksContainer';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';
export class DeletionCascading<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>, TRepositoryByEntityName extends RepositoryByEntityName<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> {
  private dependencyMap: Partial<DependencyMap<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> = {};
  private initialized: boolean = false;
  private hooksContainer: HooksContainer<DeletionCascadingHooks<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> = new HooksContainer();
  private disposableContainer: DisposableContainer = new DisposableContainer();

  /**
   * If this is > 0 than we are currently cascading a deletion.
   * This can't be a simple boolean flag since a deletion can trigger another deletion and then there would be a conflict (since the second deletion will set the flag to false, even though the first isn't finished yet)
   *
   * This is necessary for a case like this: parentParent (synchronized) -> parent (synchronized) -> child (onlylocal).
   * When deleting the parentParent it will locally remove the parent (because it's synchronized).
   * The local removal of the parent will trigger a removal of the child -> now we need to know if we actually started with a deletion so we can delete the local child.
   * Without the flag the child would not have been deleted, since we cascaded a local removal.
   */
  private isDeletingCount: number = 0;
  constructor(private readonly entityRepositoryContainer: EntityRepositoryContainer<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName, TRepositoryByEntityName>) {}
  public async init(): Promise<void> {
    if (this.initialized) {
      return;
    }
    this.dependencyMap = this.buildDependencyMap();
    const entityNamesWithDependencies = Object.keys(this.dependencyMap) as Array<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']>;
    for (const entityName of entityNamesWithDependencies) {
      this.registerEntityHooks(entityName);
    }
    this.initialized = true;
  }
  public destroy(): void {
    this.disposableContainer.disposeAll();
    this.dependencyMap = {};
    this.initialized = false;
  }
  public registerHooks(hooks: DeletionCascadingHooks<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>): Disposable {
    return this.hooksContainer.registerHooks(hooks);
  }
  private buildDependencyMap(): Partial<DependencyMap<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> {
    const map: Partial<DependencyMap<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> = {};
    this.iterateOverInfos(info => {
      const fieldInfos = Object.values(info.fieldInfos) as Array<EntityFieldInfo<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'], any, any>>;
      for (const fieldInfo of fieldInfos) {
        if (fieldInfo.ref && fieldInfo.cascadeDeleteLocally) {
          let dependencies = map[fieldInfo.ref];
          if (!dependencies) {
            dependencies = map[fieldInfo.ref] = [];
          }
          dependencies.push({
            entityName: info.entityName,
            fieldName: fieldInfo.name
          });
        }
      }
    });
    return map;
  }
  private registerEntityHooks<TEntityName extends TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']>(entityName: TEntityName): void {
    const repository = this.entityRepositoryContainer.getByEntityName(entityName);
    const hooks: EntityRepositoryHooks<TEntityTypesByEntityName[TEntityName]['entity']> = {
      afterEntityDeleted: entity => {
        this.handleEntityDeleted({
          entityName,
          entity
        });
      },
      afterEntityRemovedLocally: entity => {
        this.handleEntityRemovedLocally({
          entityName,
          entity
        });
      }
    };
    this.disposableContainer.add(repository.registerHooks(hooks));
  }
  private handleEntityDeleted(entityWithEntityName: AnyEntityWithEntityName<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>): void {
    this.isDeletingCount++;
    try {
      const dependencies = this.dependencyMap[entityWithEntityName.entityName] ?? [];
      for (const dependency of dependencies) {
        this.handleEntityRemovalOfDependency(entityWithEntityName.entity.id, dependency, removedEntityWithEntityName => {
          // typescript can't track that entityName and entity are connected, so we have to manually cast it
          this.hooksContainer.callHooks('deletionCascaded', removedEntityWithEntityName);
        });
      }
    } finally {
      this.isDeletingCount--;
    }
  }
  private handleEntityRemovedLocally(entityWithEntityName: AnyEntityWithEntityName<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>): void {
    const dependencies = this.dependencyMap[entityWithEntityName.entityName] ?? [];
    for (const dependency of dependencies) {
      this.handleEntityRemovalOfDependency(entityWithEntityName.entity.id, dependency, () => {});
    }
  }
  private handleEntityRemovalOfDependency(entityId: EntityManagerBaseEntity['id'], dependency: Dependency<TSynchronizationEnvironmentTypes, TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], TEntityTypesByEntityName>, afterDeletionCascaded: (entityWithEntityName: AnyEntityWithEntityName<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>) => void): void {
    const repository = this.entityRepositoryContainer.getByEntityName(dependency.entityName);
    const entities = repository.getAllIncludingInvisibleEntities();
    for (const entity of entities) {
      // typescript complains that the types are not overlapping since it can't know which dataType "entity[dependency.fieldName]" actually has
      // thats why we have to cast it to unknown to convince typescript that it is fine and we trust the configuration
      if (entity[dependency.fieldName] as unknown === entityId) {
        if (entity.onlyLocal && this.isDeletingCount) {
          repository.delete(entity);
        } else {
          repository.removeLocally(entity);
        }
        afterDeletionCascaded({
          entityName: dependency.entityName,
          entity
        });
      }
    }
  }
  private iterateOverInfos(cb: (info: SpecificInfo<TSynchronizationEnvironmentTypes, TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], TEntityTypesByEntityName>) => void): void {
    for (const repository of this.entityRepositoryContainer.getAll()) {
      cb(repository.getEntityInfo());
    }
  }
}
export type DeletionCascadingHooks<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = {
  /**
   * gets called when an entity gets removed locally because the deletion got cascaded
   */
  deletionCascaded?: (entityWithEntityName: AnyEntityWithEntityName<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>) => void;
};
type DependencyMap<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = { [key in TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]: Array<Dependency<TSynchronizationEnvironmentTypes, key, TEntityTypesByEntityName>> };
type Dependency<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityName extends TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = {
  entityName: TEntityName;
  fieldName: keyof TEntityTypesByEntityName[TEntityName]['entity'];
};
type SpecificInfo<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityName extends TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = EntityInfo<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'], TEntityName, TEntityTypesByEntityName[TEntityName]['entity']>;